import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { TextField, FormControl, InputLabel, Button, Box, Paper, FormHelperText } from '@mui/material';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useAuth } from '@contexts/AuthContext';
import API from '@utils/axios';
import { formError } from '@utils/helpers';
import { createPasswordValidator } from '@utils/passwordValidation';
import FormSkeleton from '@components/FormSkeleton';
import LangSelect from '@components/LangSelect';
import PasswordField from '@components/PasswordField';
import UserApiKey from '@components/user/UserApiKey';

function AccountForm() {
    const { t } = useTranslation();
    const { showMessage } = useSnackbar();
    const { refreshAuth } = useAuth();
    const [loading, setLoading] = useState(true);
    const [apiKeyData, setApiKeyData] = useState(null);

    const validationSchema = Yup.object({
        first_name: Yup.string()
            .max(255, t('Must be {{max}} characters or less', { max: 255 }))
            .required(t('This field is required')),
        last_name: Yup.string()
            .max(255, t('Must be {{max}} characters or less', { max: 255 }))
            .required(t('This field is required')),
        email: Yup.string()
            .email(t('Invalid email format'))
            .max(255, t('Must be {{max}} characters or less', { max: 255 }))
            .required(t('This field is required')),
        lang: Yup.string()
            .required(t('This field is required')),
        password: createPasswordValidator(t, false),
        password_confirmation: Yup.string()
            .oneOf([Yup.ref('password'), null], t('Passwords do not match')),
    });

    const { control, handleSubmit, setValue, setError, trigger, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        reValidateMode: 'onBlur',
    });

    const fetchData = useCallback(async () => {
        try {
            const response = await API.get('/account');
            setValue('first_name', response.data.first_name);
            setValue('last_name', response.data.last_name);
            setValue('email', response.data.email);
            setValue('lang', response.data.lang);
            setValue('password', '');
            setValue('password_confirmation', '');
            setApiKeyData({
                api_key: response.data.api_key,
                api_key_created_at: response.data.api_key_created_at,
                api_key_used_at: response.data.api_key_used_at,
            });
            setLoading(false);
        } catch (error) {
            showMessage(t('Failed to load data'), 'error');
            console.error(error);
            setLoading(false);
        }
    }, [setValue, t, showMessage]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onSubmit = async (data) => {
        try {
            if (data.password === '') {
                delete data.password;
            }
            if (typeof data.password_confirmation !== 'undefined') {
                delete data.password_confirmation;
            }
            const response = await API.put('/account', data);
            showMessage(t(response.data.message), 'success');
            // Vider les mots de passe des champs
            setValue('password', '');
            setValue('password_confirmation', '');
            setApiKeyData(null);
            refreshAuth();
        } catch (error) {
            formError(error, t, setError, showMessage);
        }
    };

    if (loading) return <FormSkeleton nb={6} />;

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Paper sx={{ p: 2, mt: 2 }}>

                <Box mb={3}>
                    <Controller
                        name="first_name"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                required
                                fullWidth
                                label={t('First name')}
                                variant="standard"
                                error={Boolean(errors.first_name)}
                                helperText={errors.first_name?.message}
                            />
                        )}
                    />
                </Box>
                <Box mb={3}>
                    <Controller
                        name="last_name"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                required
                                fullWidth
                                label={t('Last name')}
                                variant="standard"
                                error={Boolean(errors.last_name)}
                                helperText={errors.last_name?.message}
                            />
                        )}
                    />
                </Box>
                <Box mb={3}>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                required
                                fullWidth
                                label={t('Email')}
                                variant="standard"
                                error={Boolean(errors.email)}
                                helperText={errors.email?.message}
                            />
                        )}
                    />
                </Box>

                <Box mb={3}>
                    <Controller
                        name="lang"
                        control={control}
                        render={({ field }) => (
                            <FormControl
                                {...field}
                                fullWidth
                                variant="standard"
                                required
                                error={Boolean(errors.lang)}
                            >
                                <InputLabel>{t('Language')}</InputLabel>
                                <LangSelect
                                    emptyLabel={t('- No selection -')}
                                    {...field}
                                />
                                {errors.lang ? <FormHelperText>{errors.lang.message}</FormHelperText> : ''}
                            </FormControl>
                        )}
                    />
                </Box>

                <Box mb={3}>
                    <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                            <PasswordField
                                field={field}
                                error={Boolean(errors.password)}
                                helperText={errors.password?.message}
                                onBlur={(e) => {
                                    field.onBlur(e);
                                    trigger('password_confirmation');
                                }}
                            />
                        )}
                    />
                </Box>

                <Box mb={3}>
                    <Controller
                        name="password_confirmation"
                        control={control}
                        render={({ field }) => (
                            <PasswordField
                                field={field}
                                error={Boolean(errors['password_confirmation'])}
                                helperText={errors['password_confirmation']?.message}
                                label={t('Confirm password')}
                                showRules={false}
                            />
                        )}
                    />
                </Box>

                <Box>
                    <UserApiKey user={apiKeyData} generateUrl={`/account/generate-api-key`} onGenerated={fetchData} />
                </Box>


            </Paper>
            <Box sx={{ mt: 2 }}>
                <Button type="submit" variant="contained" color="primary">
                    {t('Save')}
                </Button>
            </Box>
        </form>
    );
}

export default AccountForm;
